import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import React from 'react';

/**
 *  Wraps children in a new group component such as a grid
 *
 * @param {*} groupComponent
 * @param {*} children
 * @param {*} key
 */
const createGroup = (groupComponent, children, key) =>
  children.length > 0
    ? React.createElement(groupComponent, { key }, children)
    : null;

/**
 *
 * @param {*} __typename
 * @param {*} index
 */
const createComponentKey = (__typename, index) => {
  /**
   * When it's a ScrollIntoView component, it needs a unique key.
   * Sometimes it keeps the same key on the same page type, for
   * example an article with a different slug. This makes sure that
   * this component is mounted again.
   */
  if (__typename === 'ScrollIntoView') {
    return uniqueId(`${__typename}-`);
  }

  return [__typename, index].join('-');
};

/**
 *
 * @param {*} composableElementsConfig
 * @param {*} elements
 * @param {*} groupComponent
 * @param {*} isRoot
 */
const convertToReactElements = (
  composableElementsConfig,
  elements,
  groupComponent,
  isRoot = true
) => {
  // This array is used to temporarily store components that should be in a group
  let tempChildren = [];

  // This is the final array of new children
  let newChildren = [];

  elements.forEach(({ __typename, ...props }, index) => {
    const componentConfig = get(composableElementsConfig, __typename);

    if (!componentConfig) {
      // eslint-disable-next-line
      console.warn(
        `GraphQL Element with __typename ${__typename} is not supported as a composable component.`
      );
      return;
    }

    const { Component, grid } = componentConfig;
    const useGroups = grid && isRoot;

    const key = createComponentKey(__typename, index);
    const component = <Component {...props} key={key} />;

    if (useGroups) {
      // If the component must be wrapped in a group, push it onto the temporary
      // array
      tempChildren.push(component);
    } else {
      // If the component must not be wrapped in a group, create a new group with
      // the already stored components (tempChildren) and add it to the newChildren.
      // Then continue as usuai and add the component itself.
      const groupKey = ['Group', index].join('-');
      newChildren.push(
        createGroup(groupComponent, tempChildren, groupKey),
        component
      );

      // Reset the previous grid, so we can create a new one if required.
      tempChildren = [];
    }
  });

  // Close the grid if there are still components left in the tempChildren.
  newChildren.push(
    createGroup(
      groupComponent,
      tempChildren,
      ['Group', newChildren.length].join('-')
    )
  );

  // Filter the newChildren so falsy elements are removed.
  newChildren = newChildren.filter(Boolean);

  return newChildren;
};

export default convertToReactElements;
